<script setup lang="ts">
// CampaignDetailsModal.vue
import Modal from 'dashboard/components/Modal.vue';
import { ref, onBeforeUnmount, watch, nextTick, computed } from 'vue';

interface CampaignDetailsModalProps {
  isOpen: boolean;
  campaignData: {
    campaignName: string;
    hasMessage: boolean;
    originalMessage: string;
    processedMessage: string;
    placeholders: Record<string, string>;
    tabela: string;
    filtros: any[];
    sendMessage: any;
    hasData: boolean;
  };
  highlightPlaceholders: (text: string, placeholders: Record<string, string>) => string;
  translateOperator: (operator: string) => string;
  formatPlaceholder: (key: string) => string;
  getCharacterCount: (text: string) => any;
  generateSqlPreview: () => string;
}

const props = defineProps<CampaignDetailsModalProps>();
const emit = defineEmits<{ close: [] }>();

const activeTab = ref('preview');
const isComponentMounted = ref(true);

const tabs = [
  { id: 'preview', label: 'Prévia', icon: 'i-lucide-eye', description: 'Visualização final' },
  { id: 'conditions', label: 'Filtros', icon: 'i-lucide-sliders-horizontal', description: 'Condições aplicadas' },
  { id: 'template', label: 'Template', icon: 'i-lucide-code', description: 'Código original' }
];

const sqlPreview = computed(() => {
  try {
    if (typeof props.generateSqlPreview === 'function' && isComponentMounted.value) {
      return props.generateSqlPreview();
    }
    return 'Função generateSQLPreview não disponível';
  } catch (error) {
    console.error('Erro ao gerar SQL Preview:', error);
    return 'Erro ao gerar preview SQL';
  }
});

const functionsAvailable = computed(() => {
  return (
    typeof props.highlightPlaceholders === 'function' &&
    typeof props.translateOperator === 'function' &&
    typeof props.formatPlaceholder === 'function' &&
    typeof props.getCharacterCount === 'function' &&
    typeof props.generateSqlPreview === 'function'
  );
});

const handleClose = async () => {
  if (!isComponentMounted.value) return;
  await nextTick();
  if (isComponentMounted.value) {
    emit('close');
  }
};

const changeTab = async (tabId: string) => {
  if (!isComponentMounted.value) return;
  await nextTick();
  if (isComponentMounted.value) {
    activeTab.value = tabId;
  }
};

watch(() => props.isOpen, (isOpen) => {
  if (isOpen && isComponentMounted.value) {
    nextTick(() => {
      if (isComponentMounted.value) {
        activeTab.value = 'preview';
      }
    });
  }
});

onBeforeUnmount(() => {
  isComponentMounted.value = false;
  activeTab.value = 'preview';
});
</script>

<template>
  <Modal
    v-if="isOpen && isComponentMounted"
    :show="isOpen"
    @close="handleClose"
    size="medium"
  >
    <div class="flex flex-col h-[85vh] bg-white dark:bg-n-slate-1">
      <!-- Header -->
      <div class="px-6 py-5 border-b border-slate-200 dark:border-n-slate-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-xl bg-woot-500 flex items-center justify-center">
              <span class="i-lucide-megaphone h-5 w-5 text-white"></span>
            </div>
            <div>
              <h2 class="text-xl font-semibold text-slate-900 dark:text-n-slate-12">
                {{ campaignData.campaignName }}
              </h2>
              <p class="text-sm text-slate-600 dark:text-n-slate-11 mt-1">
                Detalhes da campanha de mensagens
              </p>
            </div>
          </div>
          

        </div>
      </div>

      <!-- Navigation -->
      <div class="px-6 py-4 bg-slate-50 dark:bg-n-slate-2 border-b border-slate-200 dark:border-n-slate-6">
        <div class="flex gap-2 p-1 bg-white dark:bg-n-slate-1 rounded-lg border border-slate-200 dark:border-n-slate-6">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="changeTab(tab.id)"
            :class="[
              'flex-1 flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 text-sm font-medium',
              activeTab === tab.id
                ? 'bg-woot-500 text-white shadow-sm'
                : 'text-slate-600 dark:text-n-slate-11 hover:text-slate-900 dark:hover:text-n-slate-12 hover:bg-slate-50 dark:hover:bg-n-slate-3'
            ]"
          >
            <span :class="[tab.icon, 'h-4 w-4']"></span>
            <div class="flex flex-col items-start">
              <span>{{ tab.label }}</span>
              <span class="text-xs opacity-75 font-normal">{{ tab.description }}</span>
            </div>
          </button>
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex-1 overflow-hidden">
        <div class="h-full overflow-y-auto">
          <div class="p-6 space-y-6">
            
            <!-- Tab: Preview -->
            <div v-if="activeTab === 'preview'">
              <div v-if="!campaignData.hasMessage" class="flex items-center justify-center min-h-[400px]">
                <div class="text-center">
                  <div class="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-100 dark:bg-n-slate-3 flex items-center justify-center">
                    <span class="i-lucide-message-circle h-8 w-8 text-slate-400 dark:text-n-slate-9"></span>
                  </div>
                  <h3 class="text-lg font-semibold text-slate-900 dark:text-n-slate-12 mb-2">
                    Nenhuma mensagem configurada
                  </h3>
                  <p class="text-slate-600 dark:text-n-slate-11">
                    Esta campanha não possui um template de mensagem.
                  </p>
                </div>
              </div>

              <div v-else class="space-y-6">
                

                <!-- Message Preview -->
                <div class="bg-slate-50 dark:bg-n-slate-2 rounded-2xl p-6 border border-slate-200 dark:border-n-slate-6">
                  <div class="flex items-center gap-2 mb-4">
                    <span class="i-lucide-smartphone h-4 w-4 text-slate-500 dark:text-n-slate-11"></span>
                    <span class="text-sm font-medium text-slate-600 dark:text-n-slate-11">
                      Como o cliente verá
                    </span>
                  </div>
                  
                  <div class="bg-white dark:bg-n-slate-1 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-n-slate-6">
                    <div class="flex items-center gap-2 mb-3 pb-3 border-b border-slate-100 dark:border-n-slate-6">
                      <div class="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                        <span class="i-lucide-building h-4 w-4 text-white"></span>
                      </div>
                      <div>
                        <div class="text-sm font-medium text-slate-900 dark:text-n-slate-12">Sua Empresa</div>
                        <div class="text-xs text-slate-500 dark:text-n-slate-11">Agora</div>
                      </div>
                    </div>
                    
                    <div class="text-slate-900 dark:text-n-slate-12 leading-relaxed whitespace-pre-wrap">
                      {{ campaignData.processedMessage }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab: Conditions -->
            <div v-if="activeTab === 'conditions'">
              <div v-if="!campaignData.hasData" class="flex items-center justify-center min-h-[400px]">
                <div class="text-center">
                  <div class="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-100 dark:bg-n-slate-3 flex items-center justify-center">
                    <span class="i-lucide-database-x h-8 w-8 text-slate-400 dark:text-n-slate-9"></span>
                  </div>
                  <h3 class="text-lg font-semibold text-slate-900 dark:text-n-slate-12 mb-2">
                    Dados indisponíveis
                  </h3>
                  <p class="text-slate-600 dark:text-n-slate-11">
                    Os dados de filtro não puderam ser carregados.
                  </p>
                </div>
              </div>

              <div v-else class="space-y-6">
                <!-- Data Source -->
                <div class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-slate-800 dark:bg-n-slate-12 flex items-center justify-center">
                      <span class="i-lucide-database h-4 w-4 text-woot-600 dark:text-woot-400"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Fonte de Dados</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">Tabela de origem</p>
                    </div>
                  </div>
                  
                  <div class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-3 border border-slate-200 dark:border-n-slate-6">
                    <code class="text-blue-700 dark:text-blue-400 font-mono text-sm">{{ campaignData.tabela }}</code>
                  </div>
                </div>

                <!-- Filters -->
                <div class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-lg bg-yellow-500 flex items-center justify-center">
                        <span class="i-lucide-filter h-4 w-4 text-white"></span>
                      </div>
                      <div>
                        <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Filtros Aplicados</h3>
                        <p class="text-sm text-slate-600 dark:text-n-slate-11">Condições de seleção</p>
                      </div>
                    </div>
                    
                    <div class="px-3 py-1 rounded-full bg-woot-500 text-white text-sm font-medium">
                      {{ campaignData.filtros.length }}
                    </div>
                  </div>

                  <div v-if="campaignData.filtros.length === 0" class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6 text-center">
                    <span class="i-lucide-globe h-6 w-6 text-slate-400 dark:text-n-slate-9 mx-auto mb-2 block"></span>
                    <p class="text-slate-600 dark:text-n-slate-11">
                      Nenhum filtro - todos os registros serão selecionados
                    </p>
                  </div>

                  <div v-else class="space-y-3">
                    <div
                      v-for="(filtro, index) in campaignData.filtros"
                      :key="index"
                      class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6"
                    >
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Campo</label>
                          <div class="mt-1 bg-white dark:bg-n-slate-1 rounded-md px-3 py-2 border border-slate-200 dark:border-n-slate-6">
                            <code class="text-slate-900 dark:text-n-slate-12 font-mono text-sm">{{ filtro.attribute_key }}</code>
                          </div>
                        </div>
                        <div>
                          <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Operador</label>
                          <div class="mt-1 text-slate-900 dark:text-n-slate-12 font-medium">
                            {{ typeof translateOperator === 'function' ? translateOperator(filtro.filter_operator) : filtro.filter_operator }}
                          </div>
                        </div>
                        <div>
                          <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Valor</label>
                          <div class="mt-1 bg-white dark:bg-n-slate-1 rounded-md px-3 py-2 border border-slate-200 dark:border-n-slate-6">
                            <code class="text-slate-900 dark:text-n-slate-12 font-mono text-sm">{{ filtro.values }}</code>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Send Settings -->
                <div class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                      <span class="i-lucide-send h-4 w-4 text-white"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Configurações de Envio</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">Campos de contato</p>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6">
                      <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Campo Nome</label>
                      <div class="mt-2">
                        <code class="text-slate-900 dark:text-n-slate-12 font-mono">
                          {{ campaignData.sendMessage.name_column || 'Não configurado' }}
                        </code>
                      </div>
                    </div>

                    <div class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6">
                      <label class="text-xs font-medium text-slate-500 dark:text-n-slate-11 uppercase tracking-wide">Campo Telefone</label>
                      <div class="mt-2">
                        <code class="text-slate-900 dark:text-n-slate-12 font-mono">
                          {{ campaignData.sendMessage.phone_column || 'Não configurado' }}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- SQL Preview -->
                <div class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-slate-800 dark:bg-n-slate-12 flex items-center justify-center">
                      <span class="i-lucide-code h-4 w-4 text-white dark:text-n-slate-1"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Query SQL</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">Consulta equivalente</p>
                    </div>
                  </div>
                  
                  <div class="bg-slate-900 dark:bg-slate-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                    <pre>{{ sqlPreview }}</pre>
                  </div>
                  
                  <div v-if="!functionsAvailable" class="mt-3 p-3 bg-red-50 dark:bg-red-900 rounded-lg border border-red-200 dark:border-red-800">
                    <p class="text-sm text-red-600 dark:text-red-400">
                      ⚠️ Algumas funções não estão disponíveis
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab: Template -->
            <div v-if="activeTab === 'template'">
              <div v-if="!campaignData.hasMessage" class="flex items-center justify-center min-h-[400px]">
                <div class="text-center">
                  <div class="w-20 h-20 mx-auto mb-6 rounded-2xl bg-slate-100 dark:bg-n-slate-3 flex items-center justify-center">
                    <span class="i-lucide-file-text h-8 w-8 text-slate-400 dark:text-n-slate-9"></span>
                  </div>
                  <h3 class="text-lg font-semibold text-slate-900 dark:text-n-slate-12 mb-2">
                    Nenhum template configurado
                  </h3>
                  <p class="text-slate-600 dark:text-n-slate-11">
                    Esta campanha não possui template.
                  </p>
                </div>
              </div>

              <div v-else class="space-y-6">
                <!-- Template -->
                <div class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                      <span class="i-lucide-code h-4 w-4 text-white"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Template Original</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">Com placeholders destacados</p>
                    </div>
                  </div>
                  
                  <div class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6">
                    <div
                      class="text-slate-900 dark:text-n-slate-12 leading-relaxed whitespace-pre-wrap font-mono text-sm"
                      v-html="typeof highlightPlaceholders === 'function' ? highlightPlaceholders(campaignData.originalMessage, campaignData.placeholders) : campaignData.originalMessage"
                    ></div>
                  </div>
                </div>

                <!-- Mapping -->
                <div v-if="Object.keys(campaignData.placeholders).length > 0" class="bg-white dark:bg-n-slate-1 rounded-xl p-6 border border-slate-200 dark:border-n-slate-6 shadow-sm">
                  <div class="flex items-center gap-3 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center">
                      <span class="i-lucide-link h-4 w-4 text-white"></span>
                    </div>
                    <div>
                      <h3 class="font-semibold text-slate-900 dark:text-n-slate-12">Mapeamento</h3>
                      <p class="text-sm text-slate-600 dark:text-n-slate-11">Placeholders e campos</p>
                    </div>
                  </div>

                  <div class="grid gap-3">
                    <div
                      v-for="([key, columnName]) in Object.entries(campaignData.placeholders)"
                      :key="key"
                      class="bg-slate-50 dark:bg-n-slate-2 rounded-lg p-4 border border-slate-200 dark:border-n-slate-6"
                    >
                      <div class="flex items-center gap-4">
                        <div class="flex-1 bg-violet-50 dark:bg-violet-900 rounded-md p-3">
                          <code class="text-violet-700 dark:text-violet-300 font-mono text-sm">
                            {{ typeof formatPlaceholder === 'function' ? formatPlaceholder(key) : '{' + key + '}' }}
                          </code>
                        </div>
                        
                        <div class="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-800 flex items-center justify-center">
                          <span class="i-lucide-arrow-right h-4 w-4 text-violet-600 dark:text-violet-400"></span>
                        </div>
                        
                        <div class="flex-1 bg-slate-100 dark:bg-n-slate-3 rounded-md p-3">
                          <code class="text-slate-900 dark:text-n-slate-12 font-mono text-sm">
                            {{ columnName }}
                          </code>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-slate-200 dark:border-n-slate-6 bg-white dark:bg-n-slate-1">
        <div class="flex justify-end">
          <button
            @click="handleClose"
            class="px-6 py-2.5 text-sm font-medium text-slate-700 dark:text-n-slate-11 hover:text-slate-900 dark:hover:text-n-slate-12 bg-slate-100 dark:bg-n-slate-3 hover:bg-slate-200 dark:hover:bg-n-slate-4 rounded-lg transition-colors duration-200 border border-slate-300 dark:border-n-slate-6"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgb(203 213 225) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgb(203 213 225);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgb(148 163 184);
}

.dark .overflow-y-auto {
  scrollbar-color: rgb(var(--slate-6)) transparent;
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgb(var(--slate-6));
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background-color: rgb(var(--slate-7));
}

:deep(.bg-woot-100) {
  background-color: rgb(237 233 254);
  color: rgb(109 40 217);
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-weight: 500;
  transition: all 0.2s;
}

.dark :deep(.bg-woot-100) {
  background-color: rgb(76 29 149);
  color: rgb(196 181 253);
}

:deep(.bg-woot-100:hover) {
  background-color: rgb(221 214 254);
  transform: scale(1.05);
}

.dark :deep(.bg-woot-100:hover) {
  background-color: rgb(88 28 135);
}
</style>